// src/Components/Modules/Purchase/PurchaseOrders.js
import React, { useState, useEffect } from 'react';
import { AxiosAuthInstance } from '../../../AxiosInterceptors';
import Breadcrumb from '../../Partials/Breadcrumb';
import {
    Box, Typography, Paper, Button, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
    DialogActions, Chip, IconButton, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { FaPlus, FaTrash, FaCheck, FaTimes, FaTruck } from 'react-icons/fa';
import Swal from 'sweetalert2';

export default function PurchaseOrders() {
    const [orders, setOrders] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [items, setItems] = useState([{ item_name: '', quantity: 1, unit_price: 0 }]);
    const [form, setForm] = useState({ vendor_id: '', order_date: new Date().toISOString().split('T')[0], expected_date: '', notes: '' });

    const fetchData = async () => {
        const [oRes, vRes] = await Promise.all([
            AxiosAuthInstance.get('purchase-orders'),
            AxiosAuthInstance.get('vendors')
        ]);
        setOrders(oRes.data.orders || []);
        setVendors(vRes.data.vendors || []);
    };

    useEffect(() => { fetchData(); }, []);

    const addItem = () => setItems([...items, { item_name: '', quantity: 1, unit_price: 0 }]);
    const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
    const updateItem = (idx, field, val) => {
        const newItems = [...items];
        newItems[idx][field] = val;
        setItems(newItems);
    };

    const totalAmount = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);

    const handleSave = async () => {
        try {
            await AxiosAuthInstance.post('purchase-orders', { ...form, items });
            Swal.fire({ icon: 'success', title: 'PO Created!', timer: 1500, showConfirmButton: false });
            setDialogOpen(false);
            setItems([{ item_name: '', quantity: 1, unit_price: 0 }]);
            fetchData();
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed' });
        }
    };

    const handleStatusChange = async (id, status) => {
        try {
            await AxiosAuthInstance.post(`purchase-orders/${id}/status/${status}`);
            Swal.fire({ icon: 'success', title: `Status: ${status}`, timer: 1500, showConfirmButton: false });
            fetchData();
        } catch (err) { console.error(err); }
    };

    const statusColor = { draft: 'default', sent: 'info', confirmed: 'primary', received: 'success', cancelled: 'error' };

    return (
        <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
            <Breadcrumb title="Purchase Orders" />
            <Paper sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h5" fontWeight={700}>Purchase Orders (RFQ to PO)</Typography>
                <Button variant="contained" startIcon={<FaPlus />} onClick={() => setDialogOpen(true)}>New PO</Button>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                            <TableCell>PO #</TableCell>
                            <TableCell>Vendor</TableCell>
                            <TableCell>Order Date</TableCell>
                            <TableCell>Expected Date</TableCell>
                            <TableCell align="right">Total</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {orders.map((o) => (
                            <TableRow key={o.id} hover>
                                <TableCell><code>{o.po_number}</code></TableCell>
                                <TableCell>{o.vendor?.vendor_name || '-'}</TableCell>
                                <TableCell>{o.order_date}</TableCell>
                                <TableCell>{o.expected_date || '-'}</TableCell>
                                <TableCell align="right">৳{Number(o.total_amount).toLocaleString()}</TableCell>
                                <TableCell><Chip label={o.status} size="small" color={statusColor[o.status]} /></TableCell>
                                <TableCell align="center">
                                    {o.status === 'draft' && <IconButton size="small" onClick={() => handleStatusChange(o.id, 'sent')} sx={{ color: '#2196f3' }} title="Send"><FaTruck /></IconButton>}
                                    {o.status === 'sent' && <IconButton size="small" onClick={() => handleStatusChange(o.id, 'confirmed')} sx={{ color: '#4caf50' }} title="Confirm"><FaCheck /></IconButton>}
                                    {o.status === 'confirmed' && <IconButton size="small" onClick={() => handleStatusChange(o.id, 'received')} sx={{ color: '#8bc34a' }} title="Received"><FaCheck /></IconButton>}
                                    {o.status === 'draft' && <IconButton size="small" onClick={async () => {
                                        if(await Swal.fire({title: 'Delete?', icon: 'warning', showCancelButton: true}).then(r => r.isConfirmed)) {
                                            await AxiosAuthInstance.delete(`purchase-orders/${o.id}`);
                                            fetchData();
                                        }
                                    }} sx={{ color: '#f44336' }}><FaTrash /></IconButton>}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>New Purchase Order</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Vendor</InputLabel>
                            <Select value={form.vendor_id} label="Vendor" onChange={(e) => setForm({...form, vendor_id: e.target.value})}>
                                <MenuItem value="">Select Vendor</MenuItem>
                                {vendors.map(v => <MenuItem key={v.id} value={v.id}>{v.vendor_name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Order Date" type="date" size="small" value={form.order_date} onChange={(e) => setForm({...form, order_date: e.target.value})} fullWidth InputLabelProps={{shrink: true}} />
                            <TextField label="Expected Date" type="date" size="small" value={form.expected_date} onChange={(e) => setForm({...form, expected_date: e.target.value})} fullWidth InputLabelProps={{shrink: true}} />
                        </Box>
                        
                        <Typography variant="subtitle2" fontWeight={600} sx={{mt: 1}}>Items</Typography>
                        {items.map((item, idx) => (
                            <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <TextField size="small" label="Item Name" value={item.item_name} onChange={(e) => updateItem(idx, 'item_name', e.target.value)} sx={{flex: 3}} />
                                <TextField size="small" type="number" label="Qty" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', Number(e.target.value))} sx={{flex: 1}} />
                                <TextField size="small" type="number" label="Price" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', Number(e.target.value))} sx={{flex: 1}} />
                                <TextField size="small" label="Total" value={(item.quantity * item.unit_price).toFixed(2)} disabled sx={{flex: 1}} />
                                <IconButton onClick={() => removeItem(idx)} disabled={items.length === 1}><FaTrash size={14} /></IconButton>
                            </Box>
                        ))}
                        <Button variant="outlined" size="small" startIcon={<FaPlus />} onClick={addItem} sx={{alignSelf: 'flex-start'}}>Add Item</Button>
                        
                        <Box sx={{ p: 2, bgcolor: '#f0f4f8', borderRadius: 1, textAlign: 'right' }}>
                            <Typography variant="h6" fontWeight={700}>Total: ৳{totalAmount.toLocaleString()}</Typography>
                        </Box>
                        <TextField label="Notes" multiline rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} fullWidth />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>Save PO</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}